import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {clsx} from 'clsx';
import copyToClipboard from 'copy-to-clipboard';
import {isUndefined} from 'lodash';
import React, {memo, useState} from 'react';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, Label, Popup} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {getSweepDisplayName} from '../../util/getSweepDisplayName';
import {isMobile} from '../../util/panelbankFlow';
import {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {versionGte} from '../../util/version';
import AlertsManagementModal from '../AlertsManagementModal';
import ColorPicker from '../ColorPicker';
import DeleteRunModal from '../DeleteRunModal';
import {MultiStateCheckbox} from '../MultiStateCheckbox';
import StopRunModal from '../StopRunModal';
import SubmitToBenchmarkModal from '../SubmitToBenchmarkModal';
import {InstrumentedLoader} from '../utility/InstrumentedLoader';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {
  RunSelectorRowHeaderProps,
  useRunSelectorRowHeaderProps,
} from './hooks/useRunSelectorRowHeaderProps';
import LaunchJobIcon from './LaunchJobIcon';
import {MoveRunDrawer} from './MoveRunDrawer/MoveRunDrawer';
import {RowHeaderLabel} from './RowHeaderLabel';
import {RowHeaderMenu} from './RowHeaderMenu/RowHeaderMenu';
import {RowHeaderVisCheckbox} from './RowHeaderVisCheckbox';
import {RunPendingResultsPopup} from './RunPendingResultsPopup';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {useRunSelectorFeatureDisabled} from './RunSelectorFeaturesContext';
import {RunSelectorInteractTracker} from './RunSelectorInteractWrapper';

function isRunStoppable(row: WBTableRowWithRunSetInfo): boolean {
  // TODO(adrnswanberg): Centralize this with similar logic in RunPageActions.tsx,
  // probably around when we start using the same `Run` type everywhere.
  const cliVersion = row._wandb['value.cli_version'] ?? row._wandb.cli_version;
  if (cliVersion == null || typeof cliVersion !== 'string') {
    return false;
  }

  const isJupyterRun =
    row._wandb['value.is_jupyter_run'] ?? row._wandb.is_jupyter_run ?? false;

  return versionGte(cliVersion, '0.8.6') && !isJupyterRun;
}

const StyledLoader = styled(InstrumentedLoader)`
  margin-left: -4px;
`;
const colorPickerStyle = {marginRight: '6px', marginBottom: '8px'};

type AllRunSelectorRowHeaderProps = RunSelectorRowHeaderProps &
  ReturnType<typeof useRunSelectorRowHeaderProps>;

const RunSelectorRowHeaderInnerComp: React.FC<AllRunSelectorRowHeaderProps> = ({
  benchmarkMeta,
  className,
  closeDeleteModal,
  closeStopModal,
  customRunColor,
  customRunName,
  customRunNamesRef,
  deleteModalOpen,
  disableRunLink,
  displayBenchmark,
  displayTempCheckbox,
  entityName,
  groupedBy,
  isAnyCellSelected,
  isGroup,
  isGroupExpanded,
  isLaunchActive,
  isLaunchPublicQueueActive,
  isRunGroup,
  isSingleMode,
  isTableCollapsed,
  loadingChildren,
  onGroupButtonClick,
  onSelectRunName,
  onTempCheckboxClick,
  openDeleteModal,
  openStopModal,
  projectName,
  recursionDepth,
  row,
  runIsInBounds,
  setCustomRunColor,
  setDeleteModalOpen,
  setLaunchDrawerRow,
  showArtifactCounts,
  showLogCounts,
  stopModalOpen,
  stopRun,
  tempCheckboxState,
  tempSelectToggle,
  toggleSelection,
  truncationType,
  uniqueId,
  visCheckboxState,
  groupSelectionsRef,
  selectedCount,
}) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);
  const [moveRunDrawerOpen, setMoveRunDrawerOpen] = useState(false);
  const isDisabled = useRunSelectorFeatureDisabled();

  function onCopyRunName() {
    const name = customRunName || row.displayName;
    copyToClipboard(name);
    toast('Copied run name to clipboard');
  }

  function onCopyRunPath() {
    const fullPath = `${entityName}/${projectName}/${row.name}`;
    copyToClipboard(fullPath);
    toast('Copied run path to clipboard');
  }

  const canStopRun =
    row.state === 'running' && (row.sweep != null || isRunStoppable(row));

  const groupCounts = row.groupCounts ?? [];

  let runCount;
  let groupCount;
  if (groupCounts.length >= 2) {
    groupCount = groupCounts[groupCounts.length - 2];
    runCount = groupCounts[groupCounts.length - 1];
  } else if (groupCounts.length === 1) {
    [runCount] = groupCounts;
  }

  // Show color picker whenever we're not grouped, or when we're at
  // the deepest grouping level.
  const shouldShowColorPicker = isGroup
    ? groupCount == null
    : recursionDepth === 0;

  const isHeaderDropdownEmpty =
    isDisabled('edit-run-name') &&
    isDisabled('stop-run') &&
    isDisabled('delete');

  const isCheckboxDisabled =
    isDisabled('tag') && isDisabled('move') && isDisabled('delete');

  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  return (
    <>
      <MoveRunDrawer
        entityName={entityName}
        onClose={() => setMoveRunDrawerOpen(false)}
        onMovedRuns={() => setMoveRunDrawerOpen(false)}
        open={moveRunDrawerOpen}
        projectName={projectName}
        runs={[row]}
        groupSelectionsRef={groupSelectionsRef}
        selectedCount={selectedCount}
      />
      <DeleteRunModal
        entityName={entityName}
        isRunActive={row.state === 'running'}
        isOpen={deleteModalOpen}
        setIsModalOpen={setDeleteModalOpen}
        closeModal={closeDeleteModal}
        projectName={projectName}
        runDisplayName={row.displayName}
        runId={row.id}
        runName={row.name}
      />
      <StopRunModal
        shouldShowModal={stopModalOpen}
        onModalCanceled={closeStopModal}
        onStopRun={stopRun}
      />
      <RunPendingResultsPopup
        isPendingUpdates={row.pendingUpdates ?? false}
        trigger={
          <div
            className={`
          wb-tree-cell--header-row
          wb-tree-cell--name
          ${className ? className : ''}
          ${loadingChildren ? 'wb-tree-cell--loading-children' : ''}
          `}
            style={{paddingLeft: recursionDepth * 26}}>
            <RowHeaderMenu
              canBenchmarkRun={
                !!displayBenchmark &&
                row.state !== 'running' &&
                !row.benchmarkRun
              }
              canStopRun={canStopRun}
              isHeaderDropdownEmpty={Boolean(isHeaderDropdownEmpty)}
              isGroup={Boolean(isGroup)}
              onBenchmarkRun={() => setShowBenchmarkModal(true)}
              onCopyRunName={onCopyRunName}
              onCopyRunPath={onCopyRunPath}
              onDeleteRun={openDeleteModal}
              onMoveRun={() => setMoveRunDrawerOpen(true)}
              onStopRun={openStopModal}
              runId={row.id}
              runName={row.name}
              runDisplayName={row.displayName}
              customRunNamesRef={customRunNamesRef}
            />
            {!isCheckboxDisabled &&
              !isTableCollapsed &&
              (displayTempCheckbox || isAnyCellSelected ? (
                <MultiStateCheckbox
                  data-test="select-run-checkbox"
                  className="row-index-or-checkbox"
                  checked={tempCheckboxState}
                  onClick={() => {
                    tempSelectToggle();
                    onTempCheckboxClick();
                    window.analytics?.track('Run selected', {
                      location: analyticsLocation,
                    });
                  }}
                />
              ) : (
                <div className="row-index-or-checkbox row-index">•</div>
              ))}

            {!isSingleMode && !isDisabled('toggle-visibility') && (
              <RowHeaderVisCheckbox
                recursionDepth={recursionDepth}
                row={row}
                runIsInBounds={runIsInBounds}
                toggleSelection={toggleSelection}
                visCheckboxState={visCheckboxState}
              />
            )}
            {shouldShowColorPicker && (
              <ColorPicker
                invertColorsInDarkMode={false}
                activeColor={customRunColor}
                style={colorPickerStyle}
                setColor={setCustomRunColor}
                onClick={() => {
                  window.analytics?.track('Run color dropdown clicked', {
                    location: analyticsLocation,
                  });
                }}
                colorPaletteOption={colorPaletteOption}
              />
            )}
            {isGroup && (
              <>
                {loadingChildren ? (
                  <StyledLoader
                    active
                    inline
                    name="is-group-loader"
                    size="mini"
                  />
                ) : null}
                <Icon
                  role="button"
                  aria-label={
                    isGroupExpanded ? 'Collapse group' : 'Expand group'
                  }
                  style={{marginLeft: -6}}
                  className={`triangle ${isGroupExpanded ? 'down' : 'right'}`}
                  onClick={onGroupButtonClick}
                />
              </>
            )}
            <RunSelectorInteractTracker
              className={clsx({
                'run-row-name': true,
                failed: row.state === 'failed' && showLogCounts && !isGroup,
                'single-mode': isSingleMode && !isGroup,
              })}
              runSelector={uniqueId}>
              <RowHeaderLabel
                customSweepName={
                  row?.sweep && row.sweep.name
                    ? getSweepDisplayName({
                        ...row.sweep,
                        name: row.sweep.name,
                      })
                    : null
                }
                customRunName={customRunName}
                disableRunLink={Boolean(disableRunLink)}
                entityName={entityName}
                groupedBy={groupedBy}
                isGroup={Boolean(isGroup)}
                isRunGroup={isRunGroup}
                isSingleMode={Boolean(isSingleMode)}
                onSelectRunName={name => {
                  if (onSelectRunName) {
                    onSelectRunName(name);
                  }
                }}
                projectName={projectName}
                row={row}
                truncationType={truncationType}
              />
            </RunSelectorInteractTracker>
            {(isLaunchActive || isLaunchPublicQueueActive) &&
              row.state !== 'running' &&
              !isMobile() && (
                <div style={{marginLeft: '10px'}}>
                  <LaunchJobIcon
                    projectName={projectName}
                    entityName={entityName}
                    row={row}
                    setLaunchDrawerRow={setLaunchDrawerRow}
                    isLaunchActive={isLaunchActive}
                    isLaunchPublicQueueActive={isLaunchPublicQueueActive}
                  />
                </div>
              )}
            {isGroup && (
              <React.Fragment>
                {!isUndefined(groupCount) && (
                  <Label className="count-label">{groupCount}</Label>
                )}
                {!isUndefined(runCount) && (
                  <Label className="count-label">{runCount}</Label>
                )}
              </React.Fragment>
            )}
            {!isGroup && showArtifactCounts && (
              <>
                {row.inputArtifactsCount !== 0 && (
                  <Label className="count-label">
                    In {row.inputArtifactsCount}
                  </Label>
                )}
                {row.outputArtifactsCount !== 0 && (
                  <Label className="count-label">
                    Out {row.outputArtifactsCount}
                  </Label>
                )}
              </>
            )}

            {!isGroup && showLogCounts && row.logLineCount != null && (
              <Label className="count-label">{row.logLineCount}</Label>
            )}

            {row.state === 'running' && (
              <AlertsManagementModal
                location="runs table"
                displayIcon="active run indicator"
              />
            )}
            {row.benchmarkRun && benchmarkMeta && (
              <Popup
                hoverable={true}
                trigger={<Icon className="wbic-ic-benchmark" />}
                horizontalOffset={10}
                flowing={true}
                size="tiny"
                content={
                  <span>
                    Submitted to{' '}
                    <Link
                      to={
                        '/' +
                        benchmarkMeta.entityName +
                        '/' +
                        benchmarkMeta.name +
                        '/benchmark'
                      }>
                      {benchmarkMeta.name}
                    </Link>{' '}
                    benchmark
                  </span>
                }
              />
            )}
          </div>
        }
      />
      {benchmarkMeta && (
        <SubmitToBenchmarkModal
          runId={row.id}
          run={row}
          runName={row.displayName}
          runSummary={row.summary}
          shouldShowModal={showBenchmarkModal}
          closeModal={() => setShowBenchmarkModal(false)}
          benchmarkMeta={benchmarkMeta}
          project={{name: projectName, entityName}}
        />
      )}
    </>
  );
};

const RunSelectorRowHeaderInner = memo(
  RunSelectorRowHeaderInnerComp,
  (prevProps, nextProps) => {
    if (prevProps.row.state !== nextProps.row.state) {
      return false;
    }
    if (prevProps.row.displayName !== nextProps.row.displayName) {
      return false;
    }
    if (prevProps.row.groupCounts !== nextProps.row.groupCounts) {
      return false;
    }
    const propsAreEqual = makePropsAreEqual({
      name: 'RunSelectorRowHeader',
      deep: ['groupKey', 'tempCheckboxState', 'customRunColors'],
      // Can ignore, the row remounts when run id changes. This will screw up polling though
      // We should pass the value in rather than row probably, or maybe just check if our value
      // changes here in shouldUpdate
      ignore: ['row', 'cellHoverProps'],
      ignoreFunctions: true,
      debug: false,
      verbose: true,
    });
    return propsAreEqual(prevProps, nextProps);
  }
);

export const RunSelectorRowHeader = (props: RunSelectorRowHeaderProps) => {
  const reduxProps = useRunSelectorRowHeaderProps(props);
  return <RunSelectorRowHeaderInner {...props} {...reduxProps} />;
};
